import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en.json';
import { setShopifyLang } from '@/lib/shopify';

/**
 * Lazy translation loaders. Only the detected language ships in the initial
 * JS bundle (alongside the English fallback). Other locales are fetched as
 * separate chunks on language change, saving ~80-120KB off the critical path.
 */
const LOADERS: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  es: () => import('./locales/es.json'),
  fr: () => import('./locales/fr.json'),
  it: () => import('./locales/it.json'),
  de: () => import('./locales/de.json'),
  he: () => import('./locales/he.json'),
  ru: () => import('./locales/ru.json'),
  cs: () => import('./locales/cs.json'),
  da: () => import('./locales/da.json'),
  el: () => import('./locales/el.json'),
  hu: () => import('./locales/hu.json'),
  nl: () => import('./locales/nl.json'),
  pl: () => import('./locales/pl.json'),
  pt: () => import('./locales/pt.json'),
  ro: () => import('./locales/ro.json'),
  sv: () => import('./locales/sv.json'),
};

export function getBaseLanguage(lng?: string | null) {
  return (lng || 'en').split('-')[0].toLowerCase();
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Ship English synchronously. Detected non-English locales are loaded
    // asynchronously below — the UI re-renders the moment they resolve.
    resources: { en: { translation: en } },
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'fr', 'it', 'de', 'he', 'ru', 'cs', 'da', 'el', 'hu', 'nl', 'pl', 'pt', 'ro', 'sv'],
    load: 'languageOnly',
    cleanCode: true,
    lowerCaseLng: true,
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export async function ensureLanguageBundle(lng: string): Promise<string> {
  const baseLng = getBaseLanguage(lng);
  if (baseLng === 'en') return baseLng;
  if (i18n.hasResourceBundle(baseLng, 'translation')) return baseLng;
  const loader = LOADERS[baseLng];
  if (!loader) return baseLng;
  try {
    const mod = await loader();
    i18n.addResourceBundle(baseLng, 'translation', mod.default ?? mod, true, true);
  } catch (err) {
    console.warn('[i18n] failed to load locale', baseLng, err);
  }

  return baseLng;
}

// Kick off the detected language right away — non-blocking, but starts in
// parallel with the first paint so the swap happens within a few hundred ms.
const detected = getBaseLanguage(i18n.language);
setShopifyLang(detected);
if (detected && detected !== 'en') {
  void ensureLanguageBundle(detected);
}

// Lazy-load on every language switch (e.g. via a language picker).
i18n.on('languageChanged', (lng) => {
  setShopifyLang(getBaseLanguage(lng));
  void ensureLanguageBundle(lng);
});

export default i18n;
